import type { Dispatch, FC, SetStateAction } from "react"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  Button,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
  Tooltip,
  useDisclosure,
  ModalCloseButton,
  Text,
} from "@chakra-ui/react"
import { useFormikContext } from "formik"
import invariant from "tiny-invariant"

import ArbitrumComplianceNoteModal from "createProposal/components/ArbitrumComplianceNoteModal"
import SimulationModal from "createProposal/components/SimulationModal"
import type {
  GovernorContract,
  Governor,
  GovernorParameters,
  Token,
  EditTallyProposalQuery,
} from "query/graphql"
import {
  ExecutableCallType,
  useDelegateStatementQuery,
  useTallyProposalVersionHistoryQuery,
} from "query/graphql"
import { generateOnchainDescription } from "createProposal/helpers/description"
import { useCreateTallyProposal } from "createProposal/hooks/useCreateTallyProposal"
import { usePublishProposal } from "createProposal/hooks/usePublishProposal"
import type { CreateProposalFormValues } from "createProposal/types/form"
import { TransactionState } from "web3/types/transaction"
import { useChainReference } from "web3/hooks/useChainReference"
import { useSignerStore } from "web3/providers/SignerProvider"
import { useMe } from "user/providers/MeProvider"
import { useDevice } from "common/hooks/useDevice"
import { EllipsisVertical } from "ui/components/icons/font-awesome/EllipsisVertical"
import Web3Button from "web3/components/Web3Button"
import { Rocket } from "ui/components/icons/font-awesome/Rocket"
import { ClockRotateLeft } from "ui/components/icons/font-awesome/ClockRotateLeft"
import { getDateFromNow } from "common/helpers/date"
import { TallyProposalHistory } from "tallyProposal/components/TallyProposalVersionHistory"
import {
  FeatureFlag,
  useOrganization,
} from "organization/providers/OrganizationProvider"
import type { ExecutableCallForBlockchain } from "createProposal/action/types"
import OrganizationDisclaimerModal from "organization/components/disclaimer/OrganizationDisclaimerModal"
import {
  getOrganizationDisclaimer,
  type OrganizationDisclaimerOptions,
} from "organization/constants/org-disclamer"
import { isSecurityCheckSupportedInChain } from "web3/helpers/chains"
import GenericModal from "ui/components/GenericModal"

type CreateProposalCTAProps = {
  governance: Pick<Governor, "chainId" | "id"> & {
    contracts: {
      governor: Pick<GovernorContract, "address" | "type">
    }
    parameters: Pick<GovernorParameters, "proposalThreshold">
    token: Token
  }
  isWhiteLabel: boolean
  originalProposalId?: string
  isProposalThresholdMet: boolean
  tallyProposal?: EditTallyProposalQuery["proposal"]
  governanceSlug: string
  onPublishClick: () => void
  securityChecksRan: boolean
  displaySimulationModal: boolean
  setDisplaySimulationModal: Dispatch<SetStateAction<boolean>>
  handleRunSecurityChecks: () => void
  setDisplayArbitrumComplianceNote: Dispatch<SetStateAction<boolean>>
  acceptedComplianceNote: boolean
  displayArbitrumComplianceNote: boolean
  setAcceptedComplianceNote: Dispatch<SetStateAction<boolean>>
}
type Details = CreateProposalFormValues["details"]
type Actions = CreateProposalFormValues["actions"]
type Metadata = {
  details: Details
  actions: Actions
}

enum LastClickedAction {
  Save = "save",
  Publish = "publish",
  Undefined = "undefined",
}

const CreateProposalCTA: FC<CreateProposalCTAProps> = ({
  governance,
  isWhiteLabel,
  originalProposalId,
  isProposalThresholdMet,
  governanceSlug,
  onPublishClick,
  tallyProposal,
  securityChecksRan,
  displaySimulationModal,
  setDisplaySimulationModal,
  handleRunSecurityChecks,
  setDisplayArbitrumComplianceNote,
  acceptedComplianceNote,
  displayArbitrumComplianceNote,
  setAcceptedComplianceNote,
}) => {
  const { onLittleDevice, onLargeDevice } = useDevice()
  const { organization } = useOrganization()
  const [isLoadingSaveDraft, setIsLoadingSaveDraft] = useState(false)
  const [isLoadingSubmitOnchain, setIsLoadingSubmitOnchain] = useState(false)

  const versionHistoryModalDisclouse = useDisclosure()

  const me = useMe()

  const accountAddress = me?.address

  const { isValid, values } = useFormikContext<CreateProposalFormValues>()
  const chainReference = useChainReference()
  const { signer } = useSignerStore()
  const [orgDisclaimer, setOrgDisclaimer] =
    useState<OrganizationDisclaimerOptions | null>(null)
  const [isNoActionsWarningModalOpen, setIsNoActionsWarningModalOpen] =
    useState(false)

  useEffect(() => {
    const _orgDisclaimer = organization
      ? getOrganizationDisclaimer(organization.id)
      : undefined

    if (_orgDisclaimer) {
      setOrgDisclaimer(_orgDisclaimer)

      return
    }
  }, [organization])

  const { data: delegateStatementData, refetch: delegateProfileRefetch } =
    useDelegateStatementQuery(
      {
        input: {
          address: accountAddress ?? "",
          organizationId: organization ? organization.id : "",
        },
      },
      {
        enabled:
          Boolean(accountAddress) &&
          Boolean(organization) &&
          Boolean(orgDisclaimer?.createProposal?.content),
        refetchOnWindowFocus: true,
      },
    )

  const delegateProfile =
    delegateStatementData?.delegate?.statement ?? undefined
  const hideDisclaimer = delegateProfile?.hideDisclaimer ?? false
  const isMember = delegateProfile?.isMember ?? false

  const {
    isOpen: isCreateProposalDisclaimerModalOpen,
    onOpen: onCreateProposalDisclaimerModalOpen,
    onClose: onCreateProposalDisclaimerModalClose,
  } = useDisclosure()

  const { doesImplementBravo, isError, isLoading, createTallyProposal } =
    useCreateTallyProposal(
      governance,
      isWhiteLabel,
      governanceSlug,
      originalProposalId ?? undefined,
    )

  const handlePendingSafeTx = (values: Record<string, any>) => {
    if (me?.type !== "SAFE") {
      return
    }

    createTallyProposal({
      txHash: values.safeTxHash,
      details: values.details,
      actions: values.actions,
    })
  }

  const { status, publishProposal, setMetadata } = usePublishProposal<Metadata>(
    {
      on: {
        [TransactionState.Mining]: ({ metadata, transaction }) => {
          const { hash: txHash } = transaction
          const { actions, details } = metadata

          // Update draft
          if (originalProposalId) {
            createTallyProposal({
              txHash,
              details,
              actions,
              originalProposalId,
            })

            return
          }

          // Create new draft
          createTallyProposal({
            txHash,
            details,
            actions,
          })
        },
        [TransactionState.Fail]: () => {
          setIsLoadingSubmitOnchain(false)
        },
      },
    },
    handlePendingSafeTx,
  )

  const [lastClickedAction, setLastClickedAction] = useState<LastClickedAction>(
    LastClickedAction.Undefined,
  )

  const { isFeatureFlagOn } = useOrganization()
  const shouldDisplayComplianceModal = isFeatureFlagOn(
    FeatureFlag.DisplayComplianceNotice,
  )

  const isActionsEmpty = useMemo(() => {
    return (
      values?.actions?.every(
        (action) => action.recipe === ExecutableCallType.Empty,
      ) ?? true
    )
  }, [values])

  const isSubmitDisabled =
    !isValid || isLoading || status.state === TransactionState.Mining

  const handleSubmitGovernorProposal = async (
    ignoreSecurityChecks = false,
    ignoreDisclaimer = false,
    ignoreNoActionsValidation = false,
  ) => {
    const isSecurityCheckSupported = chainReference
      ? isSecurityCheckSupportedInChain(chainReference)
      : false

    // If disclaimer exists and the user is not a member
    // or didn't select to hide it
    if (
      !ignoreDisclaimer &&
      orgDisclaimer &&
      Boolean(orgDisclaimer.createProposal?.content) &&
      accountAddress &&
      (!isMember || !hideDisclaimer)
    ) {
      onCreateProposalDisclaimerModalOpen()

      return
    }

    if (!isProposalThresholdMet) {
      onPublishClick()

      return
    }

    if (
      !isActionsEmpty &&
      !securityChecksRan &&
      !ignoreSecurityChecks &&
      isSecurityCheckSupported
    ) {
      setLastClickedAction(LastClickedAction.Publish)

      setDisplaySimulationModal(true)

      return
    }

    if (shouldDisplayComplianceModal && !acceptedComplianceNote) {
      setDisplayArbitrumComplianceNote(true)

      return
    }

    const { details, actions } = values
    const { description, title } = details

    invariant(actions, "`actions` must be defined")
    invariant(
      doesImplementBravo !== undefined,
      "`doesImplementBravo` must be defined",
    )

    // Validate if the proposal has no actions to display a confirm dialog
    const actionsCount = actions.filter(
      (action) => action.recipe !== ExecutableCallType.Empty,
    ).length

    if (actionsCount === 0 && !ignoreNoActionsValidation) {
      setIsNoActionsWarningModalOpen(true)

      return
    }

    // Fixes a bug where a [EmptyAction, TransferAction] causes two executable calls instead of one
    const actionsToEncode =
      actions.length === 1
        ? actions
        : actions.filter((action) => action.recipe !== ExecutableCallType.Empty)

    const encodedActions = actionsToEncode.reduce((result, action) => {
      const encoded = action.encodeForBlockchain()

      if (!encoded) return result

      if (Array.isArray(encoded)) return [...result, ...encoded]

      return [...result, encoded]
    }, [] as ExecutableCallForBlockchain[])

    const onchainDescription = generateOnchainDescription(title, description)

    invariant(
      encodedActions,
      "'encodedActions' must be defined to submit proposal onchain.",
    )

    const nextMetadata = {
      actions: actionsToEncode,
      details: { ...details, description: onchainDescription },
    }

    setMetadata(nextMetadata)

    setIsLoadingSubmitOnchain(true)

    window.onbeforeunload = null

    publishProposal({
      signer,
      governance,
      chainReference,
      formValues: {
        description: onchainDescription,
        calldatas: encodedActions.map((action) => action.calldata),
        signatures: encodedActions
          .filter((action) => Boolean(action.signature))
          .map((action) => action.signature as string),
        targets: encodedActions.map((action) => action.target),
        values: encodedActions.map((action) => action.value),
      },
      metadata: nextMetadata,
    })
  }

  const handleSaveTallyProposal = () => {
    const { actions, details } = values
    const { title, description } = details

    const onchainDescription = generateOnchainDescription(title, description)

    setIsLoadingSaveDraft(true)

    // If originalProposalId, then update the existing proposal
    if (originalProposalId) {
      createTallyProposal({
        actions,
        details: { ...details, description: onchainDescription },
        originalProposalId,
      })

      return
    }

    // Otherwise, create a new proposal
    createTallyProposal({
      actions,
      details: { ...details, description: onchainDescription },
    })
  }

  useEffect(() => {
    if (isError) {
      setIsLoadingSaveDraft(false)
    }
  }, [isError])

  const getLastEditLabel = () => {
    if (tallyProposal) {
      const lastEditDateFromNow = tallyProposal
        ? getDateFromNow(tallyProposal.createdAt, false)
        : undefined

      if (tallyProposal?.creator?.name) {
        return `last edit ${lastEditDateFromNow} by ${tallyProposal.creator.name}`
      } else {
        return `last edit ${lastEditDateFromNow}`
      }
    }

    return undefined
  }
  const lastEditLabel = getLastEditLabel()

  const handleOpenVersionHistoryModal = () => {
    versionHistoryModalDisclouse.onOpen()
  }

  const handleSkipSecurityChecks = useCallback(() => {
    // trigger send proposal
    switch (lastClickedAction) {
      case LastClickedAction.Publish:
        handleSubmitGovernorProposal(true)
        break
    }

    setDisplaySimulationModal(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastClickedAction, setDisplaySimulationModal])

  useEffect(() => {
    // necessary pattern because setState is async
    if (acceptedComplianceNote && !displayArbitrumComplianceNote) {
      handleSubmitGovernorProposal(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acceptedComplianceNote, displayArbitrumComplianceNote])

  return (
    <>
      <Stack
        backgroundColor="gray.50"
        borderTop="1px solid"
        borderTopColor="gray.200"
        bottom={0}
        boxShadow="inner"
        direction="row"
        display={onLittleDevice}
        left={0}
        position="fixed"
        px={5}
        py={4}
        right={0}
        width="100%"
        zIndex={5}
      >
        {originalProposalId ? ( // Only needed for now if there is Version History to display
          <Menu>
            <MenuButton as={Button}>
              <Icon as={EllipsisVertical} fill="gray.600" pr={2} />
            </MenuButton>
            <MenuList>
              {originalProposalId ? (
                <MenuItem
                  as={Button}
                  leftIcon={<Icon as={ClockRotateLeft} />}
                  my={0}
                  onClick={handleOpenVersionHistoryModal}
                >
                  Version History
                </MenuItem>
              ) : null}
            </MenuList>
          </Menu>
        ) : null}
        <Button
          isDisabled={isSubmitDisabled}
          isLoading={isLoadingSaveDraft && isSubmitDisabled}
          variant="secondary"
          width="100%"
          onClick={() => {
            handleSaveTallyProposal()
          }}
        >
          Save draft
        </Button>
        <Web3Button
          isDisabled={isSubmitDisabled}
          isLoading={isLoadingSubmitOnchain && isSubmitDisabled}
          leftIcon={<Icon as={Rocket} fill="white" />}
          px={6}
          requiredChainId={governance.chainId}
          variant="primary"
          width="100%"
          onValid={() => {
            handleSubmitGovernorProposal(false)
          }}
        >
          Publish
        </Web3Button>
      </Stack>
      <Stack direction="row" display={onLargeDevice}>
        {originalProposalId ? (
          <Tooltip label={lastEditLabel}>
            <Button
              bgColor="white"
              variant="secondary"
              onClick={handleOpenVersionHistoryModal}
            >
              <Icon as={ClockRotateLeft} />
            </Button>
          </Tooltip>
        ) : null}

        <Tooltip isDisabled={!isSubmitDisabled} label="Please fix all errors">
          <Button
            bgColor="white"
            isDisabled={isSubmitDisabled}
            isLoading={isLoadingSaveDraft && isSubmitDisabled}
            variant="secondary"
            onClick={() => {
              handleSaveTallyProposal()
            }}
          >
            Save draft
          </Button>
        </Tooltip>

        {isSubmitDisabled ? (
          <Tooltip label="Please fix all errors">
            <Button
              isDisabled
              leftIcon={<Icon as={Rocket} fill="white" />}
              px={6}
              variant="primary"
            >
              Publish
            </Button>
          </Tooltip>
        ) : (
          <Web3Button
            isDisabled={isSubmitDisabled}
            isLoading={isLoadingSubmitOnchain && isSubmitDisabled}
            leftIcon={<Icon as={Rocket} fill="white" />}
            px={6}
            requiredChainId={governance.chainId}
            variant="primary"
            onValid={() => {
              handleSubmitGovernorProposal(false)
            }}
          >
            Publish
          </Web3Button>
        )}
      </Stack>
      {originalProposalId ? (
        <VersionHistoryModal
          governanceSlug={governanceSlug}
          isOpen={versionHistoryModalDisclouse.isOpen}
          originalProposalId={originalProposalId}
          onClose={versionHistoryModalDisclouse.onClose}
        />
      ) : null}
      <SimulationModal
        handleRunSecurityChecks={handleRunSecurityChecks}
        handleSkipSecurityChecks={handleSkipSecurityChecks}
        isOpen={displaySimulationModal && !securityChecksRan}
      />
      <ArbitrumComplianceNoteModal
        displayArbitrumComplianceNote={displayArbitrumComplianceNote}
        setAcceptedComplianceNote={setAcceptedComplianceNote}
        setDisplayArbitrumComplianceNote={setDisplayArbitrumComplianceNote}
      />
      {organization &&
      orgDisclaimer &&
      orgDisclaimer.createProposal?.content ? (
        <OrganizationDisclaimerModal
          delegateProfile={delegateProfile}
          delegateProfileRefetch={delegateProfileRefetch}
          isDisclaimerModalOpen={isCreateProposalDisclaimerModalOpen}
          modalContent={orgDisclaimer.createProposal.content}
          organization={organization}
          onDisclaimerModalClose={onCreateProposalDisclaimerModalClose}
          onSuccess={() => handleSubmitGovernorProposal(false, true)}
        />
      ) : null}
      <GenericModal
        footer={
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={4}
            width="full"
          >
            <Button
              colorScheme="gray"
              variant="outline"
              onClick={() => {
                setIsNoActionsWarningModalOpen(false)
              }}
            >
              Add actions
            </Button>
            <Button
              leftIcon={<Icon as={Rocket} fill="white" />}
              variant="primary"
              onClick={() => {
                setIsNoActionsWarningModalOpen(false)
                handleSubmitGovernorProposal(false, false, true)
              }}
            >
              Publish
            </Button>
          </Stack>
        }
        isOpen={isNoActionsWarningModalOpen}
        maxWidth="450px"
        title="Proposal without actions"
        onClose={() => {
          setIsNoActionsWarningModalOpen(false)
        }}
      >
        <Text>
          {`This proposal doesn't include any action. Are you sure you want to publish it without them?`}
        </Text>
      </GenericModal>
    </>
  )
}

const MAX_HISTORY_VERSIONS = 10

type VersionHistoryModalProps = {
  isOpen: boolean
  onClose: () => void
  originalProposalId: string
  governanceSlug: string
}
const VersionHistoryModal: FC<VersionHistoryModalProps> = ({
  isOpen,
  onClose,
  originalProposalId,
  governanceSlug,
}) => {
  const { isLoading, data } = useTallyProposalVersionHistoryQuery({
    input: {
      id: originalProposalId,
    },
  })

  const tallyProposalWithVersions =
    data?.proposalWithVersions?.slice(0, MAX_HISTORY_VERSIONS) ?? []

  return (
    <Modal isOpen={isOpen} onClose={() => onClose()}>
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent maxWidth={460}>
        <ModalHeader
          borderBottomColor="gray.100"
          borderBottomWidth={1}
          textStyle="h5"
        >
          Version History
        </ModalHeader>
        <ModalCloseButton mt={2} />
        <ModalBody px={2}>
          {isLoading ? (
            <Stack minH="sm" p={1} px={0} rounded="sm" spacing={3}>
              <Skeleton h="2.784375em" w="full" />
              <Skeleton h="2.784375em" w="full" />
              <Skeleton h="2.784375em" w="full" />
              <Skeleton h="2.784375em" w="full" />
            </Stack>
          ) : (
            tallyProposalWithVersions.map((tallyProposal, idx) => (
              <TallyProposalHistory
                key={`tally-proposal-${tallyProposal.id}`}
                isExternal
                currentDraftId={idx === 0 ? tallyProposal.id : ""}
                governanceSlug={governanceSlug}
                isLatestDraft={idx === 0}
                originalProposalId={originalProposalId}
                tallyProposal={tallyProposal}
              />
            ))
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default CreateProposalCTA
